import { authFetch, fetchWithAuth, BASE_BACKEND_URL, isAuthRedirectError } from '../auth'
import React, { useState, useEffect, useCallback, useRef } from 'react'
import cookie from 'react-cookies'
import PosDownloadView from './PosDownloadView'
import PosDownloadContext from './context/PosDownloadContext'
import usePosReportOptions from './usePosReportOptions'
import { MUI_TABLE_PAGINATION_OPTIONS } from '../components/MuiTablePagination'

const isCancelled = (error) => error?.name === 'AbortError' || isAuthRedirectError(error)

export default function PosDownloadApp() {
  const [pagination, setPagination] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    const currentPage = Number(params.get('page')) || 1
    const size = Number(params.get('per_page'))
    return {
      current_page: currentPage,
      per_page: MUI_TABLE_PAGINATION_OPTIONS.includes(size) ? size : 25,
      total_pages: 1,
      total_count: 0
    }
  })
  const reportOptions = usePosReportOptions()
  const [reportsData, setReportsData] = useState([])
  const [taskType, setTaskType] = useState(null)
  const [isFetching, setIsFetching] = useState(true)
  const [initialLoading, setInitialLoading] = useState(true)
  const [reportsError, setReportsError] = useState('')
  const [failedCreation, setFailedCreation] = useState(false)
  const [failedCreationMessage, setFailedCreationMessage] = useState('')
  const reportRequest = useRef(null)
  const reportSequence = useRef(0)
  const mounted = useRef(false)
  const mutationControllers = useRef(new Set())

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      reportRequest.current?.abort()
      mutationControllers.current.forEach((request) => request.abort())
    }
  }, [])

  const fetchReportsFromServer = useCallback(
    async (background = false) => {
      reportRequest.current?.abort()
      const controller = new AbortController()
      reportRequest.current = controller
      const sequence = ++reportSequence.current
      if (!background) setIsFetching(true)
      try {
        const params = new URLSearchParams({
          page: pagination.current_page,
          per_page: pagination.per_page,
        })
        const data = await fetchWithAuth(`${BASE_BACKEND_URL}/pos_downloads?${params}`, {
          signal: controller.signal
        })
        if (controller.signal.aborted || sequence !== reportSequence.current || !data) return
        const count = Number(data.count) || 0
        const totalPages = Math.max(1, Math.ceil(count / pagination.per_page))
        setPagination((current) => ({
          ...current,
          total_count: count,
          total_pages: totalPages,
          current_page: Math.min(current.current_page, totalPages)
        }))
        setReportsData(data.result || [])
        setReportsError('')
      } catch (error) {
        if (!isCancelled(error) && !controller.signal.aborted)
          setReportsError('No se pudieron cargar los reportes. Intenta de nuevo.')
      } finally {
        if (!controller.signal.aborted && sequence === reportSequence.current) {
          setIsFetching(false)
          setInitialLoading(false)
        }
      }
    },
    [pagination.current_page, pagination.per_page]
  )

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    params.set('page', String(pagination.current_page))
    params.set('per_page', String(pagination.per_page))
    window.history.replaceState(null, '', `${window.location.pathname}?${params}`)
    fetchReportsFromServer()
    return () => reportRequest.current?.abort()
  }, [fetchReportsFromServer])

  useEffect(() => {
    if (!reportsData.some((report) => report.status === 'pending')) return undefined
    const interval = setInterval(() => fetchReportsFromServer(true), 30000)
    return () => clearInterval(interval)
  }, [fetchReportsFromServer, reportsData])

  const handleDeleteReport = async (model, event) => {
    event.preventDefault()
    const controller = new AbortController()
    mutationControllers.current.add(controller)
    try {
      const response = await authFetch(`${BASE_BACKEND_URL}/pos_downloads/${model.id}`, {
        method: 'DELETE',
        signal: controller.signal,
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' }
      })
      if (!response || !mounted.current) return
      if (!response.ok) throw new Error('Delete failed')
      await fetchReportsFromServer()
    } catch (error) {
      if (!isCancelled(error) && mounted.current) {
        setFailedCreationMessage('No se pudo eliminar el reporte. Intenta de nuevo.')
        setFailedCreation(true)
      }
    } finally {
      mutationControllers.current.delete(controller)
    }
  }

  return (
    <PosDownloadContext.Provider
      value={{
        remoteFilterPages: reportOptions.pages,
        configureReportFilters: reportOptions.configure,
        onFilterSearch: reportOptions.search,
        onFilterOptionGroupChange: reportOptions.selectOptionGroup,
        onFilterLoadMore: reportOptions.loadMore,
        onFilterRetry: reportOptions.retry,
        surveysData: (reportOptions.pages.survey?.options || []).map((item) => ({
          uid: item.value,
          name: item.label
        })),
        reportsData,
        pagination,
        fetchDataStatus: { surveys: Boolean(reportOptions.pages.survey?.loading) },
        initialLoading,
        reportsError,
        fetchReportsFromServer,
        fetchSurveysFromServer: reportOptions.configureSurveys,
        handleDeleteReport,
        onPageChange: (_, page) => setPagination((current) => ({ ...current, current_page: page })),
        onRowsPerPageChange: (_, size) =>
          setPagination((current) => ({ ...current, per_page: size, current_page: 1 })),
        taskType,
        setTaskType,
        isFetching,
        failedCreation,
        setFailedCreation,
        failedCreationMessage,
        setFailedCreationMessage
      }}
    >
      <PosDownloadView />
    </PosDownloadContext.Provider>
  )
}
